/**
 * Extract a targeted ~200-word passage from a large RAG chunk that best
 * corresponds to the text the LLM actually cited with its [N] marker.
 *
 * Uses the LLM answer text to identify the claim surrounding [N], then
 * slides a window across the chunk scoring by word overlap. O(C) where
 * C is the chunk word count (~800).
 */

const DEFAULT_MAX_WORDS = 200;
const DEFAULT_CLAIM_WORDS = 60;
const MIN_WORD_LENGTH = 3;

/**
 * Strip processing-pipeline page boundary markers (`## Page N`, `## Page N (OCR)`)
 * that are injected by OcrJobService. These markers do not exist in the actual
 * PDF text layer or Textract geometry, so they break citation highlighting.
 */
export function stripPageMarkers(text: string): string {
  return text
    .replace(/##\s*Page\s+\d+(?:\s*\(OCR\))?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Extract the claim text that precedes [citationNumber] in the LLM answer.
 * Returns ~claimWords words immediately before the marker.
 */
function extractClaimText(
  llmAnswer: string,
  citationNumber: number,
  claimWords: number,
): string | null {
  const marker = `[${citationNumber}]`;
  const markerIdx = llmAnswer.lastIndexOf(marker);
  if (markerIdx < 0) return null;

  const textBefore = llmAnswer.substring(0, markerIdx);
  const words = textBefore.trim().split(/\s+/);
  return words.slice(-claimWords).join(' ');
}

/**
 * Build a set of significant words from the claim text for O(1) lookups.
 */
function buildWordSet(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length >= MIN_WORD_LENGTH),
  );
}

/**
 * Normalise a single word for comparison.
 */
function normaliseWord(word: string): string {
  return word.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Given the full LLM answer, a 1-indexed citation number, and the full RAG
 * chunk text, extract a ~maxWords window from the chunk that best matches
 * the LLM's cited claim.
 *
 * Returns the original chunk if it is already short enough, or a truncated
 * fallback if the marker is not found (e.g. during streaming).
 */
export function extractCitationExcerpt(
  llmAnswer: string,
  citationNumber: number,
  fullChunkText: string,
  options?: { maxWords?: number; claimWords?: number },
): string {
  const maxWords = options?.maxWords ?? DEFAULT_MAX_WORDS;
  const claimWordsCount = options?.claimWords ?? DEFAULT_CLAIM_WORDS;

  const chunkWords = fullChunkText.split(/\s+/);

  // Short-circuit: chunk is already within size limit
  if (chunkWords.length <= maxWords) {
    return stripPageMarkers(fullChunkText);
  }

  // Extract the claim from the LLM answer
  const claimText = extractClaimText(llmAnswer, citationNumber, claimWordsCount);
  if (!claimText) {
    // Marker not found (e.g. still streaming) — return first maxWords as fallback
    return stripPageMarkers(chunkWords.slice(0, maxWords).join(' '));
  }

  const claimWordSet = buildWordSet(claimText);
  if (claimWordSet.size === 0) {
    return stripPageMarkers(chunkWords.slice(0, maxWords).join(' '));
  }

  // Normalise chunk words once for reuse
  const normalisedChunkWords = chunkWords.map(normaliseWord);

  // Compute initial window score
  let currentScore = 0;
  for (let i = 0; i < maxWords && i < normalisedChunkWords.length; i++) {
    if (claimWordSet.has(normalisedChunkWords[i])) {
      currentScore++;
    }
  }

  let bestScore = currentScore;
  let bestStart = 0;

  // Slide the window forward one word at a time
  for (let start = 1; start + maxWords <= chunkWords.length; start++) {
    const leaving = normalisedChunkWords[start - 1];
    if (claimWordSet.has(leaving)) currentScore--;

    const entering = normalisedChunkWords[start + maxWords - 1];
    if (claimWordSet.has(entering)) currentScore++;

    if (currentScore > bestScore) {
      bestScore = currentScore;
      bestStart = start;
    }
  }

  return stripPageMarkers(chunkWords.slice(bestStart, bestStart + maxWords).join(' '));
}

/**
 * Result of excerpt extraction including the word offset within the chunk,
 * used to resolve which page the excerpt actually falls on when chunks span
 * multiple pages.
 */
export interface ExcerptResult {
  text: string;
  /** Word offset of the excerpt window start within the full chunk. */
  wordOffset: number;
  /** Total word count of the full chunk. */
  totalWords: number;
}

/**
 * Like {@link extractCitationExcerpt} but also returns the word offset of the
 * best-matching window within the chunk. This allows the caller to compute
 * which page the excerpt falls on when the chunk spans multiple pages.
 */
export function extractCitationExcerptWithOffset(
  llmAnswer: string,
  citationNumber: number,
  fullChunkText: string,
  options?: { maxWords?: number; claimWords?: number },
): ExcerptResult {
  const maxWords = options?.maxWords ?? DEFAULT_MAX_WORDS;
  const claimWordsCount = options?.claimWords ?? DEFAULT_CLAIM_WORDS;

  const chunkWords = fullChunkText.split(/\s+/);
  const totalWords = chunkWords.length;

  // Short-circuit: chunk is already within size limit
  if (chunkWords.length <= maxWords) {
    return { text: stripPageMarkers(fullChunkText), wordOffset: 0, totalWords };
  }

  // Extract the claim from the LLM answer
  const claimText = extractClaimText(llmAnswer, citationNumber, claimWordsCount);
  if (!claimText) {
    return { text: stripPageMarkers(chunkWords.slice(0, maxWords).join(' ')), wordOffset: 0, totalWords };
  }

  const claimWordSet = buildWordSet(claimText);
  if (claimWordSet.size === 0) {
    return { text: stripPageMarkers(chunkWords.slice(0, maxWords).join(' ')), wordOffset: 0, totalWords };
  }

  const normalisedChunkWords = chunkWords.map(normaliseWord);

  let currentScore = 0;
  for (let i = 0; i < maxWords && i < normalisedChunkWords.length; i++) {
    if (claimWordSet.has(normalisedChunkWords[i])) {
      currentScore++;
    }
  }

  let bestScore = currentScore;
  let bestStart = 0;

  for (let start = 1; start + maxWords <= chunkWords.length; start++) {
    const leaving = normalisedChunkWords[start - 1];
    if (claimWordSet.has(leaving)) currentScore--;

    const entering = normalisedChunkWords[start + maxWords - 1];
    if (claimWordSet.has(entering)) currentScore++;

    if (currentScore > bestScore) {
      bestScore = currentScore;
      bestStart = start;
    }
  }

  // Use the midpoint of the excerpt window for page resolution
  const wordOffset = bestStart + Math.floor(maxWords / 2);

  return {
    text: stripPageMarkers(chunkWords.slice(bestStart, bestStart + maxWords).join(' ')),
    wordOffset,
    totalWords,
  };
}
